import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "../../../redux/store";
import { allIncidentsStateAction } from "../../../redux/features/incident-slice";
import { getUserState } from "../../../redux/features/auth-slice";

const IncidentLineChart = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: incidents, loading } = useSelector(
    (state: AppState) => state.incidents.allIncidentsState
  );
  const [chartOptions, setChartOptions] = useState<any>({});
  const userState = useSelector(getUserState);
  const [series, setSeries] = useState<any[]>([]);

  useEffect(() => {
    if (!userState) {
      return;
    }
    dispatch(allIncidentsStateAction());
  }, [dispatch]);

  useEffect(() => {
    if (incidents.length > 0) {
      processChartData();
    }
  }, [incidents]);

  const getStartOfWeek = (date: Date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const startOfWeek = new Date(date);
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
  };

  const processChartData = () => {
    const today = new Date();
    const weeks = Array.from({ length: 8 }, (_, i) => {
      const startOfWeek = getStartOfWeek(new Date(today));
      startOfWeek.setDate(startOfWeek.getDate() - 7 * (7 - i)); // Calculate the start of each week
      return startOfWeek;
    });

    const categories = weeks.map((_, index) => `Week ${index + 1}`);
    const technicalUnits = Array.from(
      new Set(
        incidents.flatMap((incident) =>
          incident.technicalUnits.map((unit) => unit.name)
        )
      )
    );
    const seriesData = technicalUnits.map((unit) => {
      const unitData = weeks.map((weekDate) => {
        const startOfWeek = new Date(weekDate);
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(weekDate);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        return incidents.filter(
          (incident) =>
            incident.technicalUnits.some(
              (technicalUnit) => technicalUnit.name === unit
            ) &&
            new Date(incident.occurrenceDate) >= startOfWeek &&
            new Date(incident.occurrenceDate) <= endOfWeek
        ).length;
      });

      return {
        name: unit,
        data: unitData,
      };
    });

    const options = {
      chart: {
        type: "line",
        height: 350,
        toolbar: {
          show: true,
        },
      },
      stroke: {
        width: 2,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: categories,
        title: {
          text: "Weeks",
        },
      },
      yaxis: {
        title: {
          text: "Number of Incidents",
        },
        min: 0,
        max: Math.ceil(Math.max(...series.flatMap((s) => s.data))),
        tickAmount: Math.ceil(Math.max(...series.flatMap((s) => s.data))),
        forceNiceScale: true,
        labels: {
          formatter: (value: number) => Math.floor(value),
        },
      },
      colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"],
      legend: {
        position: "bottom",
        markers: {
          radius: 12,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
    };

    setChartOptions(options);
    setSeries(seriesData);
  };

  if (loading) return <div>Loading chart data...</div>;

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <h2 className="text-lg font-semibold mb-4">
        Incidents by Technical Unit (Last 8 Weeks)
      </h2>
      <ReactApexChart
        options={chartOptions}
        series={series}
        type="line"
        height={350}
      />
    </div>
  );
};

export default IncidentLineChart;
